import { User } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, Heading, Menu, MenuItem, Popover, Text } from '@/components/core';
import { AddIcon, CloseIcon } from '@/components/icons';
import { Profile, RoleBadge } from '@/components/ui';
import { toProfileId, useBatchGetProfiles, useListUsers } from '@/hooks/api';
import { Notification, useValidationConfigStore } from '@/store';
import { DivProps } from '@/utils';

import { Cron, FrequencySetting } from './FrequencySetting';

export type EmailNotificationConfigurationProps = DivProps & {
  id: string;
};

const IEmailNotificationConfiguration = ({ id, className, ...rest }: EmailNotificationConfigurationProps) => {
  const updateNotification = useValidationConfigStore((state) => state.updateNotification);
  const notification = useValidationConfigStore((state) => state.getNotification(id));
  const { data: users } = useListUsers();
  const { data: profiles } = useBatchGetProfiles(users?.data.map((user) => toProfileId(user.profileId)));

  const updateEmailSettings = useCallback(
    ({ emails }: Pick<Notification, 'emails'>) => {
      updateNotification(id, (notification) => {
        const changes = {} as Partial<Notification>;
        if (emails) changes.emails = emails;
        /* Add default when and timezone settings if not present */
        if (notification.when == null) changes.when = new Cron().cronExpression();
        if (notification.timezone == null) changes.timezone = new Cron().timezone;
        return { ...notification, ...changes };
      });
    },
    [id, updateNotification]
  );

  const addUser = useCallback(
    (user: User) => {
      const profile = profiles?.find((p) => p.profileId === user.profileId);
      if (profile) {
        const emails = new Set(notification?.emails ?? []);
        emails.add(profile.email);
        updateEmailSettings({ emails: Array.from(emails) });
      }
    },
    [notification?.emails, profiles, updateEmailSettings]
  );

  const deleteEmail = useCallback(
    (email: string) => {
      if (notification?.emails) {
        const emails = new Set(notification.emails);
        emails.delete(email);
        updateEmailSettings({ emails: Array.from(emails) });
      }
    },
    [notification?.emails, updateEmailSettings]
  );

  const availableUsers = useMemo(() => {
    return (users?.data ?? []).filter((user) => {
      const profile = profiles?.find((p) => p.profileId === user.profileId);
      return profile && !notification?.emails?.includes(profile.email);
    });
  }, [notification?.emails, profiles, users?.data]);

  return (
    <div className={merge('flex flex-col gap-4 p-4', className)} {...rest}>
      <div className="flex flex-col">
        <Heading size="xs">Email</Heading>
        <Text>Send email when a document is flagged for review.</Text>
      </div>

      <Flex variant="secondary" className="gap-2 rounded-lg p-2">
        <MenuTrigger>
          <Button isDisabled={!availableUsers.length} variant="secondary" className="w-fit rounded-sm !p-0.5">
            <AddIcon />
          </Button>
          <Popover>
            <Menu>
              {availableUsers.map((user) => (
                <MenuItem key={user.userId} onAction={() => addUser(user)}>
                  <Profile
                    profileId={user.profileId}
                    props={{ img: { className: 'size-6' } }}
                    disableTooltip
                    showName
                  />
                  {user.roleIds.map((roleId) => (
                    <RoleBadge key={roleId} roleId={roleId} />
                  ))}
                </MenuItem>
              ))}
            </Menu>
          </Popover>
        </MenuTrigger>
        {notification?.emails?.map((email) => (
          <Flex variant="secondary" className="items-center gap-1 rounded-sm px-1 py-0.5">
            <Text size="xs">{email}</Text>
            <Button variant="plain" className="p-0" onPress={() => deleteEmail(email)}>
              <CloseIcon className="!size-3" />
            </Button>
          </Flex>
        ))}
      </Flex>

      <FrequencySetting id={id} />
    </div>
  );
};

export const EmailNotificationConfiguration = memo(
  IEmailNotificationConfiguration
) as typeof IEmailNotificationConfiguration;
